import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );

    const productLink =
      productCard?.getProductCardLink() ||
      hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();

    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );

    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener(
      'change',
      this.#closeQuickAddModal
    );

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
      {
        signal: this.#cartUpdateAbortController.signal,
      }
    );

    document.addEventListener(
      ThemeEvents.variantSelected,
      this.#updateQuickAddButtonState.bind(this)
    );

    this.#loadCartState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener(
      'change',
      this.#closeQuickAddModal
    );

    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();

    document.removeEventListener(
      ThemeEvents.variantSelected,
      this.#updateQuickAddButtonState.bind(this)
    );
  }

  /**
   * Clears cached content and synchronizes the GRADI button state
   * whenever the cart changes.
   */
  #handleCartUpdate = (event) => {
    this.#cachedContent.clear();

    if (event?.detail?.data?.didError) return;

    const cart = event?.detail?.resource;

    if (cart && Array.isArray(cart.items)) {
      this.#syncButtonWithCart(cart);
    } else {
      this.#loadCartState();
    }
  };

  /**
   * Loads the current cart so the button is correct
   * when the page first loads or is refreshed.
   */
  async #loadCartState() {
    try {
      const root = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) return;

      const cart = await response.json();

      this.#syncButtonWithCart(cart);
    } catch (error) {
      console.warn(
        'GRADI: Unable to synchronize quick-add cart state.',
        error
      );
    }
  }

  /**
   * Keeps AÑADIR / AÑADIDO synchronized
   * with the real contents of the cart.
   *
   * @param {Object} cart
   */
  #syncButtonWithCart(cart) {
    const variantId = String(
      this.dataset.variantId || ''
    );

    if (!variantId || !Array.isArray(cart?.items)) return;

    const isInCart = cart.items.some(
      (item) =>
        String(item.variant_id ?? item.id) === variantId
    );

    const button = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.quick-add__button--add')
    );

    if (!button) return;

    const text = button.querySelector(
      '.add-to-cart-text__content > span > span'
    );

    if (text) {
      text.textContent = isInCart
        ? 'AÑADIDO'
        : 'AÑADIR';
    }

    button.disabled = isInCart;

    button.setAttribute(
      'aria-disabled',
      isInCart ? 'true' : 'false'
    );

    if (isInCart) {
      this.dataset.inCart = 'true';
    } else {
      delete this.dataset.inCart;
    }
  }

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml
   */
  #updateVariantPicker(newHtml) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (!modalContent) return;

    const variantPicker = /** @type {VariantPicker} */ (
      modalContent.querySelector('variant-picker')
    );

    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click.
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    let productGrid =
      this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html =
        await this.fetchProductPage(currentUrl);

      if (html) {
        const gridElement =
          html.querySelector(
            '[data-product-grid-content]'
          );

        if (gridElement) {
          productGrid = /** @type {Element} */ (
            gridElement.cloneNode(true)
          );

          this.#cachedContent.set(
            currentUrl,
            productGrid
          );
        }
      }
    }

    if (productGrid) {
      const freshContent =
        /** @type {Element} */ (
          productGrid.cloneNode(true)
        );

      await this.updateQuickAddModal(
        freshContent
      );

      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    )
      return;

    const productDetails =
      dialogComponent.querySelector(
        '.product-details'
      );

    const productMedia =
      dialogComponent.querySelector(
        '.product-information__media'
      );

    productDetails?.scrollTo({
      top: 0,
      behavior: 'instant',
    });

    productMedia?.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(
    dialogComponent
  ) {
    this.toggleAttribute(
      'stay-visible',
      true
    );

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () =>
        this.toggleAttribute(
          'stay-visible',
          false
        ),
      {
        once: true,
      }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    )
      return;

    this.#stayVisibleUntilDialogCloses(
      dialogComponent
    );

    dialogComponent.showDialog();

    const dialog =
      dialogComponent.refs?.dialog;

    if (!dialog) return;

    dialog.addEventListener(
      'animationstart',
      this.#resetScroll.bind(this),
      {
        once: true,
      }
    );
  };

  #closeQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    )
      return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content.
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();

    this.#abortController =
      new AbortController();

    try {
      const response = await fetch(
        productPageUrl,
        {
          signal:
            this.#abortController.signal,
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`
        );
      }

      const responseText =
        await response.text();

      const html =
        new DOMParser().parseFromString(
          responseText,
          'text/html'
        );

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid
   */
  async updateQuickAddModal(productGrid) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (!productGrid || !modalContent)
      return;

    if (isMobileBreakpoint()) {
      const productDetails =
        productGrid.querySelector(
          '.product-details'
        );

      const productFormComponent =
        productGrid.querySelector(
          'product-form-component'
        );

      const variantPicker =
        productGrid.querySelector(
          'variant-picker'
        );

      const productPrice =
        productGrid.querySelector(
          'product-price'
        );

      const productTitle =
        document.createElement('a');

      productTitle.textContent =
        this.dataset.productTitle || '';

      productTitle.href =
        this.productPageUrl;

      const productHeader =
        document.createElement('div');

      productHeader.classList.add(
        'product-header'
      );

      productHeader.appendChild(
        productTitle
      );

      if (productPrice) {
        productHeader.appendChild(
          productPrice
        );
      }

      productGrid.appendChild(
        productHeader
      );

      if (variantPicker) {
        productGrid.appendChild(
          variantPicker
        );
      }

      if (productFormComponent) {
        productGrid.appendChild(
          productFormComponent
        );
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(
      modalContent
    );
  }

  /**
   * Updates the quick-add button state
   * based on whether a swatch is selected.
   *
   * @param {VariantSelectedEvent} event
   */
  #updateQuickAddButtonState(event) {
    if (
      !(event.target instanceof HTMLElement)
    )
      return;

    if (
      event.target.closest('product-card') !==
      this.closest('product-card')
    )
      return;

    const productOptionsCount =
      this.dataset.productOptionsCount;

    const quickAddButton =
      productOptionsCount === '1'
        ? 'add'
        : 'choose';

    this.setAttribute(
      'data-quick-add-button',
      quickAddButton
    );
  }

  /**
   * Syncs the variant selection from the product card to the modal.
   *
   * @param {Element} modalContent
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId =
      this.#getSelectedVariantId();

    if (!selectedVariantId) return;

    const modalInputs =
      modalContent.querySelectorAll(
        'input[type="radio"][data-variant-id]'
      );

    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId ===
          selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;

        input.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );

        break;
      }
    }
  }
}

if (
  !customElements.get(
    'quick-add-component'
  )
) {
  customElements.define(
    'quick-add-component',
    QuickAddComponent
  );
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      ThemeEvents.cartUpdate,
      this.handleCartUpdate,
      {
        signal:
          this.#abortController.signal,
      }
    );

    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#updateProductTitleLink
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  /**
   * Closes the dialog.
   * @param {CartUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError)
      return;

    this.closeDialog();
  };

  #updateProductTitleLink = (
    /** @type {CustomEvent} */ event
  ) => {
    const anchorElement =
      /** @type {HTMLAnchorElement} */ (
        event.detail.data.html?.querySelector(
          '.view-product-title a'
        )
      );

    const viewMoreDetailsLink =
      /** @type {HTMLAnchorElement} */ (
        this.querySelector(
          '.view-product-title a'
        )
      );

    const mobileProductTitle =
      /** @type {HTMLAnchorElement} */ (
        this.querySelector(
          '.product-header a'
        )
      );

    if (!anchorElement) return;

    if (viewMoreDetailsLink)
      viewMoreDetailsLink.href =
        anchorElement.href;

    if (mobileProductTitle)
      mobileProductTitle.href =
        anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion =
      getIOSVersion();

    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (iosVersion.major === 16 &&
        iosVersion.minor >= 4)
    )
      return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid =
        document.querySelector(
          '#ResultsList [product-grid-view]'
        );

      if (grid) {
        const currentWidth =
          grid.getBoundingClientRect().width;

        grid.style.width =
          `${currentWidth - 1}px`;

        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (
  !customElements.get(
    'quick-add-dialog'
  )
) {
  customElements.define(
    'quick-add-dialog',
    QuickAddDialog
  );
}